import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { signUp, verifyCallbackURL } from "@/api/auth";
import { sessionQuery } from "@/api/session";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/field";
import { AuthLayout } from "@/features/auth/auth-layout";
import { SocialSignIn, isSocialError, socialProblemText } from "@/features/auth/social-sign-in";
import { checkNewPassword, passwordErrorOf } from "@/features/auth/password";
import { noindexHead } from "@/features/marketing/seo";
import { socialSignInProblem } from "@bridewell/shared";
import { t } from "@/i18n";
import { LEGAL } from "@/legal/config";
import { safeInternalPath } from "@/lib/utils";

export const Route = createFileRoute("/signup")({
  head: () => noindexHead(t("marketing.seo.signup")),
  // `error`: an Apple or Google sign-up that came back without an account (social-sign-in.tsx).
  validateSearch: z.object({ redirect: z.string().optional().transform(safeInternalPath), error: z.string().max(80).optional() }),
  beforeLoad: async ({ context, search }) => {
    if (await context.queryClient.ensureQueryData(sessionQuery)) throw redirect({ to: search.redirect ?? "/app" });
  },
  component: SignupPage,
});

function SignupPage() {
  const { redirect: target, error: socialError } = Route.useSearch();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(isSocialError(socialError) ? socialProblemText(socialSignInProblem(socialError) ?? "failed", "signUp") : null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    // The API runs the same check; asking first saves a round trip and keeps the form as it is.
    const weak = checkNewPassword(password, { email, name });
    if (weak) { setError(weak); return; }
    setBusy(true); setError(null);
    // No session is created until the email is verified; the API mails a link instead.
    const res = await signUp.email({ name, email, password, callbackURL: verifyCallbackURL(target) });
    setBusy(false);
    if (res.error) { setError(passwordErrorOf(res.error) ?? res.error.message ?? t("auth.signUpFailed")); return; }
    setSent(true);
  };

  if (sent) {
    return (
      <AuthLayout
        title={t("auth.verifyTitle")}
        footer={<Link to="/login" search={{ redirect: target }} className="font-semibold text-accent">{t("auth.signIn")}</Link>}
      >
        <div className="flex flex-col gap-3">
          <p className="text-[15px] text-text-secondary">{t("auth.verifySent", { email })}</p>
          <p className="text-sm text-text-tertiary">{t("auth.verifyHint")}</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t("auth.welcome")}
      subtitle={t("auth.platforms")}
      footer={<>{t("auth.haveAccount")} <Link to="/login" search={{ redirect: target }} className="font-semibold text-accent">{t("auth.signIn")}</Link></>}
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t("auth.yourName")}><Input autoComplete="name" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label={t("common.email")}><Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label={t("auth.password")} hint={t("auth.passwordHint")}><Input type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {/* Clickwrap: an unticked, required box — the acceptance record the contract rests on. Links open in a new tab so the form survives. */}
        <label className="flex items-start gap-2.5 text-sm text-text-secondary">
          <input type="checkbox" required checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-(--accent)" />
          <span>
            {t("auth.agreeAge", { age: LEGAL.minimumAge })}{" "}
            <Link to="/legal/terms" target="_blank" className="font-semibold text-accent">{t("legal.terms")}</Link>{" "}
            {t("auth.agreeAnd")}{" "}
            <Link to="/legal/privacy" target="_blank" className="font-semibold text-accent">{t("legal.privacy")}</Link>.
          </span>
        </label>
        {error && <p className="text-sm text-danger">{error}</p>}
        <Button type="submit" size="lg" loading={busy} disabled={!agreed}>{t("auth.signUp")}</Button>
        <SocialSignIn mode="signUp" target={target} agreed={agreed} />
        <p className="text-center text-xs text-text-tertiary">{t("auth.privacy")}</p>
      </form>
    </AuthLayout>
  );
}
